import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { projects } from '../services/api';
import { nomUsuari } from '../utils/usuari';

const STATUS_LABELS = {
  all: 'Tots',
  active: 'Actiu',
  draft: 'Esborrany',
  archived: 'Arxivat'
};

function ProjectsPage() {
  const { user } = useAuth();
  const [projectList, setProjectList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ title: '', description: '' });
  const [submitting, setSubmitting] = useState(false);
  const [filter, setFilter] = useState('all');

  const canCreate = user?.role === 'admin' || user?.role === 'instructor';

  const loadProjects = useCallback(async () => {
    try {
      setLoading(true);
      const params = filter !== 'all' ? { status: filter } : {};
      const { data } = await projects.getAll(params);
      setProjectList(data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Error carregant els projectes');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await projects.create(formData);
      setShowModal(false);
      setFormData({ title: '', description: '' });
      loadProjects();
    } catch (err) {
      setError(err.response?.data?.message || 'Error creant el projecte');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-content">Projectes</h1>
        {canCreate && (
          <button
            onClick={() => setShowModal(true)}
            className="btn btn-primary"
          >
            Nou projecte
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex space-x-2">
        {['all', 'active', 'draft', 'archived'].map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-3 py-1 rounded-full text-sm font-medium ${
              filter === status
                ? 'bg-accent text-white'
                : 'bg-surface-muted text-content-secondary hover:bg-surface-muted'
            }`}
          >
            {STATUS_LABELS[status]}
          </button>
        ))}
      </div>

      {error && (
        <div className="bg-error-subtle border border-error/30 text-error-text px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      ) : projectList.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-content-muted">No s'han trobat projectes.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projectList.map((project) => (
            <Link
              key={project.id}
              to={`/projects/${project.id}`}
              className="card hover:border-border-strong transition-colors"
            >
              <div className="flex items-start justify-between">
                <h3 className="text-lg font-semibold text-content">{project.title}</h3>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  project.status === 'active' ? 'bg-success-subtle text-success-text' :
                  project.status === 'draft' ? 'bg-warning-subtle text-warning-text' :
                  'bg-surface-muted text-content'
                }`}>
                  {STATUS_LABELS[project.status] || project.status}
                </span>
              </div>
              {project.description && (
                <p className="mt-2 text-sm text-content-secondary line-clamp-2">{project.description}</p>
              )}
              <div className="mt-4 flex items-center text-sm text-content-muted">
                <span>{project.caseCount} {project.caseCount === 1 ? 'cas' : 'casos'}</span>
                <span className="mx-2">|</span>
                <span>per {nomUsuari(project.createdBy)}</span>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-surface rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-content mb-4">Crear nou projecte</h2>
              <form onSubmit={handleCreate} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-content-secondary">Títol</label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="input mt-1"
                    placeholder="Introdueix el títol del projecte"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-content-secondary">Descripció</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="input mt-1"
                    rows={3}
                    placeholder="Introdueix la descripció del projecte"
                  />
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="btn btn-secondary"
                  >
                    Cancel·lar
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="btn btn-primary disabled:opacity-50"
                  >
                    {submitting ? 'Creant...' : 'Crear'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProjectsPage;
